import { Fragment, useCallback, useState } from 'react';
import {
  Button,
  Box,
  Link,
  TextField,
  Typography,
  Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  PeopleOutline as GrowerIcon,
  ScienceOutlined as SoilIcon,
  PrintOutlined as PrintIcon,
  WarningAmberOutlined as OverlapIcon,
} from '@mui/icons-material';
import { v4 } from 'uuid';
import { mapKeys, camelCase, sortBy } from 'lodash-es';
import { Polygon as FieldIcon } from '../../icons/Polygon';
import * as service from '../../services/admin-service';
import { GeometryViewer } from '../../components/GeometryViewer';
import { useAlerts } from '../../hooks/use-alerts';
import { SimpleTable } from '../../components/table/SimpleTable';

export function Lookup() {
  const [working, setWorking] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState();
  const [geometries, setGeometries] = useState([]);
  const { showError } = useAlerts();

  const lookupField = useCallback(
    (event) => {
      event.preventDefault();
      if (!searchTerm.trim()) return;
      setWorking(true);
      setResults(null);
      service
        .lookup(searchTerm)
        .then(setResults)
        .catch(showError)
        .finally(() => setWorking(false));
    },
    [searchTerm, showError],
  );

  return (
    <>
      {buildGeometries()}
      <Box
        sx={{
          px: 2,
          py: 3,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <form onSubmit={lookupField}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 4 }}>
            <TextField
              value={searchTerm}
              disabled={working}
              onChange={(event) => setSearchTerm(event.target.value)}
              label="aaid, field id, grower id, name, whatever..."
              size="small"
              sx={{ width: 450 }}
              autoFocus
            />
            <LoadingButton
              size="large"
              color="primary"
              variant="contained"
              sx={{ height: 'calc(100% - 2px)' }}
              disabled={!searchTerm.trim().length}
              loading={working}
              onClick={lookupField}
            >
              Lookup
            </LoadingButton>
          </Box>
        </form>
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          {buildResults()}
        </Box>
      </Box>
    </>
  );

  function buildResults() {
    const allResources = [...buildGrowers(), ...buildFields()];
    const sortedResources = sortBy(allResources, ([name]) => name).map(
      ([, resource]) => resource,
    );
    return sortedResources.map((resource, i) => (
      <Box key={i}>
        {i ? <Divider sx={{ mb: 6 }} /> : null}
        {resource}
      </Box>
    ));
  }

  function buildGrowers() {
    if (!results?.growers?.length) return [];

    return results.growers.map((grower, i) => [
      grower.operationName,
      <Fragment key={i}>
        {buildDetails(grower.operationName, [grower], GrowerIcon)}
      </Fragment>,
    ]);
  }

  function buildFields() {
    if (!results?.fields?.length) return [];

    return results.fields.map((revisions, i) => {
      const { name, aaid } = revisions[0];

      return [
        name,
        <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {buildDetails(name, revisions, FieldIcon)}
          {buildDetails(`${name} Soil`, results.soil?.[aaid], SoilIcon)}
          {buildDetails(
            `${name} Overlaps`,
            results.overlaps?.[aaid],
            OverlapIcon,
          )}
        </Box>,
      ];
    });
  }

  function buildHeaderHref(row) {
    if (row.guid) {
      return `/field-boundary-management/${row.guid.toUpperCase()}`;
    }

    if (row.aaid && row.growerId) {
      return `/potential-productivity/${row.growerId.toUpperCase()}/${row.aaid.toUpperCase()}`;
    }

    if (row.interpolationId) {
      return `/soil-report`;
    }
  }

  function buildDetails(title, rows, Icon) {
    if (!rows?.length) return null;
    const keys = Object.keys(rows[0]);

    return (
      <Box>
        <Link
          href={buildHeaderHref(rows[0])}
          color="inherit"
          target="_blank"
          underline="hover"
        >
          <Typography
            variant="h6"
            sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Icon />
            {title}
          </Typography>
        </Link>
        <Box sx={{ overflow: 'hidden', width: '100%' }}>
          <SimpleTable
            size="small"
            rows={rows.map((row) =>
              Object.fromEntries(
                keys.map((key) => [
                  key,
                  <Box
                    title={JSON.stringify(row[key])}
                    key={key}
                    sx={{
                      whiteSpace: 'nowrap',
                      maxWidth: 350,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {buildCellValue(title, key, row)}
                  </Box>,
                ]),
              ),
            )}
          />
        </Box>
      </Box>
    );
  }

  function buildCellValue(source, key, row) {
    if (/(boundary|shape)$/i.test(key)) {
      return buildGeometryActions(source, row, key);
    }

    if (row.interpolationId && key === 'id') {
      return buildSoilReportPrintButton(row);
    }

    return JSON.stringify(
      row[key] === undefined ? 'undefined' : row[key],
    ).replace(/(^"|"$)/g, '');
  }

  function buildSoilReportPrintButton(row) {
    return (
      <Link
        href={`/api/soil/${row.growerId}/pdf?id=${row.interpolationId}`}
        target="_blank"
        title="Print"
        underline="none"
        sx={{
          mr: 1,
          display: 'inline-flex',
          alignItems: 'center',
          verticalAlign: 'middle',
          gap: 0.5,
        }}
      >
        <PrintIcon fontSize="small" />
        {row.id}
      </Link>
    );
  }

  function buildGeometryActions(source, row, key) {
    return (
      <>
        <Button
          size="small"
          sx={{ p: 0, minWidth: 0 }}
          onClick={() => viewGeometry(source, row, row[key])}
        >
          View
        </Button>
        {' | '}
        <Button
          size="small"
          sx={{ p: 0, minWidth: 0 }}
          onClick={() => downloadGeometry(source, row, row[key])}
        >
          Download
        </Button>
      </>
    );
  }

  function buildGeometries() {
    return geometries.map((geometry) => (
      <GeometryViewer
        key={geometry.id}
        {...geometry}
        onClose={closeGeometry.bind(null, geometry.id)}
      />
    ));
  }

  function viewGeometry(source, row, geojson) {
    const id = v4();
    const rowId = row.objectid || row.OBJECTID || row.id;
    const title = `${source} ${rowId}`;
    setGeometries((current) => [
      ...current,
      { title, id, geojson, ...mapKeys(row, (_, key) => camelCase(key)) },
    ]);
  }

  function downloadGeometry(source, row) {
    const { crs, ...geojson } = row.shape;
    const filename = [
      row.aaid,
      row.aamanageduuid,
      row.AAMANAGEDID,
      row.id,
      row.objectid,
      row.OBJECTID,
    ]
      .filter(Boolean)
      .join('_');

    const blob = new Blob([JSON.stringify(geojson, null, 2)], {
      type: 'application/json;charset=utf-8',
    });
    const a = window.document.createElement('a');
    a.href = window.URL.createObjectURL(blob);
    a.setAttribute('download', filename);
    a.click();
  }

  function closeGeometry(id) {
    setGeometries((current) => current.filter((c) => c.id !== id));
  }
}
